import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { TableCell } from "@/components/ui/table";
import { Eye, FileCheck2, Send, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const BULAN = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli",
  "Agustus", "September", "Oktober", "November", "Desember"];
const fmtTime = (iso) => iso ? new Date(iso).toLocaleString("id-ID", {
  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
}) : "-";
const errMsg = (e) => e?.response?.data?.detail || e.message;

export function usePayrollSlips(year, month) {
  const [data, setData] = useState(null);
  const latest = useRef(0);
  const reload = useCallback(async () => {
    const req = ++latest.current;
    try {
      const d = await api.listPayrollSlips(year, month);
      if (req === latest.current) setData(d);
    } catch (e) { toast.error("Gagal muat slip: " + errMsg(e)); }
  }, [year, month]);
  useEffect(() => { reload(); }, [reload]);
  const byPid = Object.fromEntries((data?.slips || []).map((s) => [s.personnel_id, s]));
  return { data, byPid, reload };
}

export function SlipHeaderButtons({ year, month, data, reload }) {
  const [busy, setBusy] = useState("");
  const label = `${BULAN[month - 1]} ${year}`;
  async function generate() {
    const again = data?.generated_at
      ? "\n\nSlip bulan ini sudah pernah dibuat. Slip akan dibuat ulang dan dikirim lagi ke semua karyawan."
      : "";
    if (!window.confirm(`Generate slip gaji ${label}?\nSlip dikirim otomatis ke email karyawan 1 jam setelah ini.${again}`)) return;
    setBusy("gen");
    try {
      const r = await api.generatePayrollSlips(year, month);
      toast.success(`${r.generated} slip dibuat. Kirim otomatis ${fmtTime(r.scheduled_send_at)}`);
      reload();
    } catch (e) { toast.error("Gagal generate: " + errMsg(e)); }
    finally { setBusy(""); }
  }
  async function sendAll() {
    if (!window.confirm(`Kirim sekarang semua slip ${label} yang belum terkirim?\nKirim otomatis bulan ini akan dibatalkan.`)) return;
    setBusy("send");
    try {
      const r = await api.sendAllPayrollSlips(year, month);
      toast.success(`Terkirim ${r.sent} · Gagal ${r.failed} · Email kosong ${r.no_email}`);
      reload();
    } catch (e) { toast.error("Gagal kirim: " + errMsg(e)); }
    finally { setBusy(""); }
  }
  return (
    <>
      <Button size="sm" onClick={generate} disabled={!!busy}
        className="h-9 rounded-sm gap-1.5 bg-emerald-700 hover:bg-emerald-800" data-testid="payroll-generate-slip-btn">
        {busy === "gen" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FileCheck2 className="w-3.5 h-3.5" />} Generate Slip
      </Button>
      <Button variant="outline" size="sm" onClick={sendAll} disabled={!!busy || !data?.slips?.length}
        className="h-9 rounded-sm gap-1.5" data-testid="payroll-send-all-slip-btn">
        {busy === "send" ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Kirim Slip
      </Button>
    </>
  );
}

export function SlipBanner({ year, month, data }) {
  const slips = data?.slips || [];
  const count = (st) => slips.filter((s) => s.status === st).length;
  return (
    <div className="border border-zinc-200 bg-white rounded-sm px-3 py-2 text-[12px] text-zinc-600 flex flex-wrap gap-x-4 gap-y-1"
      data-testid="payroll-slip-banner">
      <span className="font-semibold text-zinc-800">Slip Gaji {BULAN[month - 1]} {year}</span>
      {slips.length === 0 ? <span>Belum di-generate.</span> : (
        <>
          <span>Dibuat {fmtTime(data.generated_at)}</span>
          {data.auto_send_cancelled
            ? <span className="text-zinc-500" data-testid="payroll-slip-auto-cancelled">Kirim otomatis dibatalkan (sudah dikirim manual)</span>
            : <span>Kirim otomatis {fmtTime(data.scheduled_send_at)}</span>}
          <span className="text-emerald-700">Terkirim {count("sent")}</span>
          <span className="text-amber-700">Menunggu {count("generated")}</span>
          <span className="text-red-700">Gagal {count("failed")}</span>
          <span className="text-zinc-500">Email kosong {count("no_email")}</span>
        </>
      )}
    </div>
  );
}

const STATUS = {
  generated: ["Menunggu", "bg-amber-50 text-amber-800 border-amber-200"],
  sending: ["Mengirim…", "bg-blue-50 text-blue-800 border-blue-200"],
  sent: ["Terkirim", "bg-emerald-50 text-emerald-800 border-emerald-200"],
  failed: ["Gagal", "bg-red-50 text-red-700 border-red-200"],
  no_email: ["Email kosong", "bg-zinc-100 text-zinc-600 border-zinc-300"],
};

export function SlipCell({ pid, slip, reload }) {
  const [busy, setBusy] = useState(false);
  if (!slip) return <TableCell className="text-center text-zinc-300" data-testid={`slip-cell-${pid}`}>—</TableCell>;
  const [lbl, cls] = slip.status === "generated" && slip.auto_send_cancelled
    ? ["Belum dikirim", "bg-zinc-50 text-zinc-700 border-zinc-300"]
    : STATUS[slip.status] || [slip.status, ""];
  async function view() {
    const w = window.open("", "_blank");
    try {
      const blob = await api.payrollSlipPdf(slip.id);
      const url = URL.createObjectURL(blob);
      if (w) w.location.href = url; else window.open(url, "_blank");
    } catch (e) { w?.close(); toast.error("Gagal buka slip: " + errMsg(e)); }
  }
  async function send() {
    setBusy(true);
    try {
      const r = await api.sendPayrollSlip(slip.id);
      if (r.status === "sent") toast.success(`Slip ${slip.name} terkirim ke ${r.sent_to}`);
      else toast.error(`Slip ${slip.name}: ${r.error || STATUS[r.status]?.[0]}`);
      reload();
    } catch (e) { toast.error("Gagal kirim: " + errMsg(e)); }
    finally { setBusy(false); }
  }
  const via = slip.sent_via === "gmail" ? "Gmail" : slip.sent_via === "emergent" ? `email cadangan${slip.gmail_error ? ` (Gmail: ${slip.gmail_error})` : ""}` : "";
  const title = slip.status === "sent" ? `Terkirim ke ${slip.sent_to} · ${fmtTime(slip.sent_at)}${via ? ` · via ${via}` : ""}`
    : slip.status === "generated" ? (slip.auto_send_cancelled ? "Kirim otomatis dibatalkan — kirim manual" : `Kirim otomatis ${fmtTime(slip.scheduled_send_at)}`) : slip.error || "";
  return (
    <TableCell className="text-center" data-testid={`slip-cell-${pid}`}>
      <div className="flex items-center justify-center gap-1">
        <span title={title} className={`text-[9px] px-1 py-0.5 rounded-sm border uppercase tracking-wider whitespace-nowrap ${cls}`}
          data-testid={`slip-status-${pid}`}>{lbl}</span>
        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 rounded-sm" onClick={view} title="Lihat slip"
          data-testid={`slip-view-${pid}`}><Eye className="w-3.5 h-3.5" /></Button>
        <Button variant="outline" size="sm" className="h-7 px-2 rounded-sm text-[11px]" onClick={send}
          disabled={busy || slip.status === "sending"} data-testid={`slip-send-${pid}`}>
          {busy ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Kirim"}
        </Button>
      </div>
    </TableCell>
  );
}
